// Fix 46 (2026-10-02): reporte de Clientes nuevos. Solo lectura.
// La definición de "cliente nuevo" vive en el backend
// (services/new_customers_service.py): esta pantalla solo muestra lo que
// devuelve GET /api/new-customers y descarga Excel/PDF del mismo período.
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip,
} from 'recharts';
import {
  Loader2, RefreshCw, Search, UserPlus, Repeat, DollarSign, Users,
  FileSpreadsheet, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import KpiCard from '../components/KpiCard';
import EmptyState from '../components/EmptyState';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency } from '../lib/utils';

const PAGE_SIZE = 50;
const PRESETS = ['this_week', 'last_week', 'this_month', 'last_month', 'last_30', 'this_year'];

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function presetRange(key) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = new Date(today);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  switch (key) {
    case 'this_week':
      return [isoDay(monday), isoDay(today)];
    case 'last_week': {
      const end = new Date(monday);
      end.setDate(end.getDate() - 1);
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      return [isoDay(start), isoDay(end)];
    }
    case 'last_month':
      return [
        isoDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        isoDay(new Date(today.getFullYear(), today.getMonth(), 0)),
      ];
    case 'last_30': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return [isoDay(start), isoDay(today)];
    }
    case 'this_year':
      return [isoDay(new Date(today.getFullYear(), 0, 1)), isoDay(today)];
    case 'this_month':
    default:
      return [isoDay(new Date(today.getFullYear(), today.getMonth(), 1)), isoDay(today)];
  }
}

// 'YYYY-MM-DD' → mm/dd/yyyy, sin pasar por Date para que el huso horario del
// navegador no corra el día.
// Fix 48 (2026-10-04): siempre formato estadounidense MM/DD/YYYY, en español
// y en inglés, igual que las descargas (pedido de la contadora).
function fmtDay(s, lang) {
  if (!s || s.length < 10) return '—';
  const [y, m, d] = [s.slice(0, 4), s.slice(5, 7), s.slice(8, 10)];
  return `${m}/${d}/${y}`;
}

function periodLabel(key, granularity, lang) {
  if (granularity === 'month') return `${key.slice(5, 7)}/${key.slice(0, 4)}`;
  const d = fmtDay(key, lang);
  return d.slice(0, 5);
}

export default function NewCustomersPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const qc = useQueryClient();
  const initial = presetRange('this_month');
  const [preset, setPreset] = useState('this_month');
  const [fromDate, setFromDate] = useState(initial[0]);
  const [toDate, setToDate] = useState(initial[1]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(null);

  const validRange = Boolean(fromDate && toDate && fromDate <= toDate);

  const reportQuery = useQuery({
    queryKey: ['new-customers', fromDate, toDate],
    queryFn: async () => (await api.get('/new-customers', {
      params: { from_date: fromDate, to_date: toDate },
    })).data,
    enabled: validRange,
  });
  const data = reportQuery.data;
  const summary = data?.summary || {};
  const items = useMemo(() => data?.items || [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) => [
      r.name, r.nickname, r.phone, r.city, r.state, r.first_auction, r.first_order_id,
    ].some((v) => String(v || '').toLowerCase().includes(q)));
  }, [items, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const chartData = useMemo(() => (data?.series || []).map((p) => ({
    label: periodLabel(p.period, data?.granularity, lang),
    count: p.count,
  })), [data, lang]);

  const choosePreset = (key) => {
    const [f, to] = presetRange(key);
    setPreset(key);
    setFromDate(f);
    setToDate(to);
    setPage(1);
  };

  const handleDownload = async (format) => {
    if (!validRange) return;
    try {
      setDownloading(format);
      const res = await api.get('/new-customers/export', {
        params: { format, from_date: fromDate, to_date: toDate },
        responseType: 'blob',
      });
      const type = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lovi-clientes-nuevos_${fromDate}_a_${toDate}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('new_customers.download_done'));
    } catch (err) {
      toast.error(t('new_customers.download_error'));
    } finally {
      setDownloading(null);
    }
  };

  const chartTitleSuffix = data?.granularity === 'week'
    ? t('new_customers.chart_by_week')
    : data?.granularity === 'month'
      ? t('new_customers.chart_by_month')
      : t('new_customers.chart_by_day');

  return (
    <div data-testid="new-customers-page" className="animate-fade-slide-up">
      <PageHeader
        title={t('new_customers.title')}
        subtitle={t('new_customers.subtitle')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => handleDownload('xlsx')}
              disabled={!validRange || downloading !== null}
              data-testid="new-customers-download-xlsx"
            >
              {downloading === 'xlsx'
                ? <Loader2 className="h-[14px] w-[14px] animate-spin" />
                : <FileSpreadsheet className="h-[14px] w-[14px]" />}
              {t('new_customers.download_excel')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleDownload('pdf')}
              disabled={!validRange || downloading !== null}
              data-testid="new-customers-download-pdf"
            >
              {downloading === 'pdf'
                ? <Loader2 className="h-[14px] w-[14px] animate-spin" />
                : <FileText className="h-[14px] w-[14px]" />}
              {t('new_customers.download_pdf')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => qc.invalidateQueries({ queryKey: ['new-customers'] })}
              data-testid="new-customers-refresh"
            >
              <RefreshCw className="h-[14px] w-[14px]" /> {t('common.refresh')}
            </Button>
          </div>
        }
      />

      {/* Período */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-5">
        <div className="flex flex-wrap gap-2" data-testid="new-customers-presets">
          {PRESETS.map((key) => (
            <Button
              key={key}
              size="sm"
              variant={preset === key ? 'default' : 'secondary'}
              onClick={() => choosePreset(key)}
              data-testid={`new-customers-preset-${key}`}
            >
              {t(`new_customers.preset_${key}`)}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            aria-label={t('new_customers.from')}
            title={t('new_customers.from')}
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPreset(null); setPage(1); }}
            className="w-[150px] h-[38px] text-[13px]"
            data-testid="new-customers-from"
          />
          <span className="text-muted-foreground text-[12px]">–</span>
          <Input
            type="date"
            aria-label={t('new_customers.to')}
            title={t('new_customers.to')}
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPreset(null); setPage(1); }}
            className="w-[150px] h-[38px] text-[13px]"
            data-testid="new-customers-to"
          />
        </div>
      </div>

      {!validRange && (
        <p className="mb-4 text-[13px]" style={{ color: 'var(--danger-fg)' }} data-testid="new-customers-invalid-range">
          {t('new_customers.invalid_range')}
        </p>
      )}
      {reportQuery.isError && (
        <p className="mb-4 text-[13px]" style={{ color: 'var(--danger-fg)' }}>
          {t('new_customers.load_error')}
        </p>
      )}
      {validRange && reportQuery.isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground text-[13px] py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-2" data-testid="new-customers-kpis">
            <KpiCard
              index={0}
              label={t('new_customers.kpi_new')}
              value={summary.new_customers ?? 0}
              icon={UserPlus}
              tone="success"
              testid="kpi-new-customers"
            />
            <KpiCard
              index={1}
              label={t('new_customers.kpi_repeat')}
              value={summary.repeat_customers ?? 0}
              icon={Repeat}
              testid="kpi-repeat-customers"
            />
            <KpiCard
              index={2}
              label={t('new_customers.kpi_total')}
              value={formatCurrency(summary.total_spent ?? 0, locale)}
              icon={DollarSign}
              testid="kpi-new-customers-total"
            />
            <KpiCard
              index={3}
              label={t('new_customers.kpi_existing')}
              value={summary.already_in_qbo ?? 0}
              icon={Users}
              tone="warning"
              testid="kpi-already-in-qbo"
            />
          </div>
          <p className="text-[12px] text-muted-foreground mb-6">
            {t('new_customers.definition')}{' '}
            {(summary.already_in_qbo ?? 0) > 0 && t('new_customers.existing_note')}{' '}
            {(summary.pending_data ?? 0) > 0 && (
              <span data-testid="new-customers-pending-note">
                {summary.pending_data} {t('new_customers.pending_note')}
              </span>
            )}
          </p>

          {/* Gráfico */}
          <div className="premium-card mb-6" style={{ padding: 20 }} data-testid="new-customers-chart">
            <div className="text-[14px] font-semibold mb-3">
              {t('new_customers.chart_title')} · {chartTitleSuffix}
            </div>
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ fill: 'rgba(212,168,67,0.08)' }}
                    contentStyle={{
                      background: 'hsl(var(--chart-tooltip-bg))',
                      border: '1px solid var(--chart-tooltip-border)',
                      borderRadius: 10,
                      fontSize: 12,
                      padding: '10px 14px',
                      color: 'hsl(var(--foreground))',
                      boxShadow: 'var(--shadow-md)',
                    }}
                    formatter={(v) => [v, t('new_customers.kpi_new')]}
                  />
                  <Bar dataKey="count" fill="#D4A843" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Lista */}
          <div className="relative w-full md:w-96 mb-3">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60 pointer-events-none"
              strokeWidth={1.75}
            />
            <Input
              placeholder={t('new_customers.search_placeholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
              data-testid="new-customers-search"
            />
          </div>
          {filtered.length === 0 ? (
            <EmptyState message={t('new_customers.empty')} testid="new-customers-empty" />
          ) : (
            <div className="premium-card" style={{ padding: 0 }}>
              <Table data-testid="new-customers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('new_customers.col_date')}</TableHead>
                    <TableHead>{t('new_customers.col_customer')}</TableHead>
                    <TableHead>{t('new_customers.col_nickname')}</TableHead>
                    <TableHead>{t('new_customers.col_phone')}</TableHead>
                    <TableHead>{t('new_customers.col_location')}</TableHead>
                    <TableHead>{t('new_customers.col_auction')}</TableHead>
                    <TableHead className="text-right">{t('new_customers.col_first_total')}</TableHead>
                    <TableHead className="text-right">{t('new_customers.col_orders')}</TableHead>
                    <TableHead className="text-right">{t('new_customers.col_total')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((r) => (
                    <TableRow key={r.tiktok_user_id} data-testid={`new-customer-row-${r.tiktok_user_id}`}>
                      <TableCell className="whitespace-nowrap">{fmtDay(r.first_purchase_day, lang)}</TableCell>
                      <TableCell>
                        {r.name
                          ? r.name
                          : <span className="italic text-muted-foreground">{t('new_customers.pending_name')}</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{r.nickname || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.phone || '—'}</TableCell>
                      <TableCell>{[r.city, r.state].filter(Boolean).join(', ') || '—'}</TableCell>
                      <TableCell>{r.first_auction || '—'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.first_order_total, locale)}</TableCell>
                      <TableCell className="text-right">{r.orders_count}</TableCell>
                      <TableCell className="text-right">{formatCurrency(r.total_spent, locale)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between px-4 py-3 text-[12px] text-muted-foreground">
                <span data-testid="new-customers-count">
                  {t('new_customers.showing')} {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, filtered.length)} {t('new_customers.of')} {filtered.length}
                </span>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    {t('new_customers.prev')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    {t('new_customers.next')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
